<script setup lang="ts">
const { t } = useI18n();
const { getStackedCollectibles, getStackedCollectiblesCount } =
  useCollectiblesController();
const appStateStore = useAppStateStore();
const merchant = appStateStore.currentMerchant;
const route = useRoute();
const router = useRouter();
const filterParams = computed(() => (route.query.filter as string) || "");
const GFALExStore = useGFALExStore();
const isLoadingMore = ref<boolean>(false);
const page = ref<number>(1);
const pageSize = ref<number>(12);
const showListed = ref<boolean>(route.query.listed === "false" ? false : true);

useSeoMeta({
  title: `${t(`main.appMenu.market`)} ${
    "| " + t(`main.merchant.${merchant}`)
  } GFAL Marketplace`,
});

const { data, status, execute, clear } = await useAsyncData(
  "getStackedMarketCollectibles",
  async () => {
    const setId = route.params.setId as string;
    if (!setId) throw new Error("No setId found");
    if (!GFALExStore.GFALExRate) throw new Error("Server down");

    const [collectibles, altTotal] = await Promise.all([
      getStackedCollectibles(
        merchant,
        page.value,
        pageSize.value,
        showListed.value,
        filterParams.value,
        setId
      ),
      getStackedCollectiblesCount(merchant, !showListed.value, setId),
    ]);

    return {
      collectibles,
      altTotal,
    };
  },
  {
    default() {
      return {
        collectibles: {
          records: [],
          total: 0,
          page: 0,
          totalPages: 0,
        },
        altTotal: 0,
      };
    },
    server: false,
    lazy: true,
  }
);

useInfiniteScroll(
  window,
  async () => {
    isLoadingMore.value = true;
    page.value++;
    const res = await getStackedCollectibles(
      merchant,
      page.value,
      pageSize.value,
      showListed.value,
      filterParams.value,
      (route.params.setId as string) || ""
    );
    if (!res) {
      return;
    }
    data.value.collectibles = {
      records: [...data.value.collectibles.records, ...res.records],
      total: res.total,
      page: res.page,
      totalPages: res.totalPages,
    };
    isLoadingMore.value = false;
  },
  {
    distance: 100,
    canLoadMore: () =>
      !isLoadingMore.value &&
      data.value.collectibles.total > data.value.collectibles.records.length &&
      data.value.collectibles.page < data.value.collectibles.totalPages,
  }
);

const backUrl = computed(() => {
  if (route.query.return) {
    return {
      path: route.query.return as string,
    };
  } else {
    return {
      path: `/market/${appStateStore.currentMerchant}`,
      query: {
        listed: showListed.value ? "true" : "false",
        ...(route.query.filter && { filter: route.query.filter }),
      },
    };
  }
});

const backText = computed(() => {
  return "main.storeDetail.backToList";
});

watch(filterParams, async () => {
  page.value = 1;
  clear();
  await execute();
});

watch(showListed, async () => {
  router.push({
    query: {
      ...route.query,
      listed: showListed.value ? "true" : "false",
      filter: "",
    },
  });
  page.value = 1;
  clear();
  await execute();
});
</script>

<template>
  <CommonHeroMain />
  <NuxtLinkLocale
    class="flex items-center my-7 cursor-pointer text-neutral-300 hover:text-primary-500"
    :to="backUrl">
    <NuxtIcon name="mdi:chevron-left" class="h-5 w-5 shrink-0" />
    <p class="text-xl font-bold">{{ $t(backText) }}</p>
  </NuxtLinkLocale>
  <CardCollectibleStackDetail
    :set-id="route.params.setId as string"
    :stack-view="true" />
  <div class="mt-7">
    <Toolbar
      v-if="status === 'success'"
      v-model:showListed="showListed"
      :store-items="data.collectibles.records"
      :item-number="data.collectibles.total"
      :alt-total="data.altTotal"
      :show-filters="true" />
    <LoaderToolbar v-if="status === 'pending' || status === 'idle'" />
    <CardCollectibleStackListHeader
      class="mt-8"
      :is-loading="status === 'pending' || status === 'idle'"
      :showListed="showListed" />
    <div v-if="status !== 'pending' && status !== 'idle'">
      <CommonNotFoundServer v-if="status === 'error'" />
      <CommonNotFoundItems
        v-else-if="data.collectibles.total < 1"
        :msg="$t('main.storeMain.noItemsFound')" />
    </div>
    <LoaderCardTableSkeleton
      v-if="
        status === 'pending' ||
        status === 'idle' ||
        !appStateStore.areTidsLoaded
      " />
    <div v-else-if="status !== 'error' && data.collectibles.total > 0">
      <template
        v-for="(collectible, index) in data.collectibles.records"
        :key="collectible.id">
        <CardCollectibleStackList
          :collectible="collectible"
          :index="index"
          :filter-params="filterParams" />
      </template>
    </div>
  </div>
  <div v-if="isLoadingMore" class="py-10 flex flex-col items-center gap-4">
    <p class="text-neutral-300 text-center">
      {{ $t("main.storeMain.loadingMore") }}
    </p>
    <LoaderSpinner size="2xl" />
  </div>
</template>
